"use client"

import styled from "@emotion/styled"
import { Desktop, Mobile } from "@eurodrive/ui"
import Container from "@eurodrive/ui/Container"
import { getCarTypeFromClassificationCode } from "@eurodrive/utils"
import { getUmbracoMediaUrl } from "@eurodrive/utils/getUmbracoMediaUrl"
import { ctaVehicleClicked } from "@eurodrive/utils/gtm"
import { Button, Typography } from "@mui/material"
import { SaleCarDto } from "api"
import Image from "next/image"
import ShareButton from "./ShareButton"
import { CarUmbraco } from "./extractUmbraco"

const StyledContainer = styled.div`
  padding-top: 48px;
  padding-bottom: 64px;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
  align-items: center;
  position: relative;
  text-align: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding-top: 58px;
  }
`

const ButtonsContainer = styled.div`
  display: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: block;
    position: fixed;
    bottom: 200px;
    right: 0;
    z-index: 100000;
  }
`

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 40px;
  width: 100%;
`

const DescriptionContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  text-align: center;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 500px;
  }
`

// Rendered as a ul, so the browser list defaults have to go: the 40px
// padding-inline-start pushed the whole strip off centre and left uneven space
// on the two sides.
// Mobile is a centred flex wrap rather than a grid so that a short final row
// sits in the middle instead of hanging off the inline-start edge.
// Desktop keeps the grid, with minmax(0, 1fr) rather than 1fr: auto-sized
// tracks refuse to shrink and the strip overflows the 1040px container once
// there are more than six of them
const CardsGrid = styled.div<{ itemCount: number }>`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 14px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
  ${(props) => props.theme.breakpoints.up("md")} {
    display: grid;
    grid-template-columns: repeat(
      ${({ itemCount }) => itemCount},
      minmax(0, 1fr)
    );
    /* The tracks absorb the free space while each card stays capped, so the
     * cards have to be centred inside their own track — justify-content cannot
     * do it here, an fr track leaves nothing over to distribute. */
    justify-items: center;
    gap: 28px;
  }
`

const Card = styled.div`
  display: flex;
  padding: 8px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  border-radius: 8px;
  border: 1px solid #ece9e9;
  flex: 0 1 calc((100% - 28px) / 3);
  min-width: 0;
  /* The design caps a card at 139px. Without this the percentage basis keeps
   * growing with the container, so between the phone widths and md the cards
   * ballooned well past the cap. */
  max-width: 139px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 32px 12px;
    gap: 24px;
    width: 100%;
    max-width: 139px;
  }
`

const CardTextBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  text-align: center;
`

const StyledButton = styled(Button)`
  width: 100%;
  ${(props) => props.theme.breakpoints.up("md")} {
    max-width: 328px;
  }
`

const IconbBox = styled.div`
  width: 40px;
  height: 40px;
  padding: 6px 3px;
  ${(props) => props.theme.breakpoints.up("md")} {
    padding: 0;
  }
`

const ImageContainer = styled.div`
  width: 100%;
  height: 100%;
  position: relative;
`

const DescrBox = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 500px;
`

export default function Specs({
  data,
  carUmbraco,
  secondHand,
}: {
  data: SaleCarDto
  carUmbraco?: CarUmbraco
  secondHand: boolean
}) {
  // Map classification code to carType for search URL
  const classificationCode =
    data.vehicleSingleValuedClassification?.[0]
      ?.vehicleSingleValuedClassificationCode
  const carType = getCarTypeFromClassificationCode(classificationCode)

  // Determine dealType based on carClassification
  const dealType = data.carClassification === "UsedCar" ? "used" : "new"

  // The API exposes the model year on carZone, but leaves it at 0 for some
  // records; manufacturerYear is the fallback for those
  const modelYear = data.carZone.modelYear || data.manufacturerYear

  const items = [
    ...(modelYear
      ? [
          {
            icon: "/icons/calendar.svg",
            title: "שנת דגם",
            value: `${modelYear}`,
          },
        ]
      : []),
    {
      icon: "/icons/condition.svg",
      title: "מצב הרכב",
      value: data.mileage > 150 ? data.mileage : "0 ק״מ",
    },
    {
      icon: "/icons/gearbox.svg",
      title: "תיבת הילוכים",
      value: data.transmissionTypeDescription,
    },
    {
      icon: "/icons/engineType.svg",
      title: "סוג מנוע",
      value: data.driveTypeDescription,
    },
    {
      icon: "/icons/displacement.svg",
      title: "נפח מנוע",
      value: `${data.engineCapacity} סמ"ק`,
    },
    {
      icon: "/icons/safety.svg",
      title: "ציון בטיחות",
      value: `${data.safetyEquipmentLevel} כוכבים`,
    },
    {
      icon: "/icons/pollution.svg",
      title: "דרגת זיהום",
      value: `דרגה ${data.emissionRating}`,
    },
    ...(secondHand
      ? [
          {
            icon: "/icons/calendar.svg",
            title: "מועד הטסט",
            value: `${data.carZone.spec.licenseValidation}`,
          },
        ]
      : []),
  ]

  return (
    <Container paddingMobile>
      <StyledContainer>
        <ButtonsContainer>
          <Container style={{ marginRight: "auto" }}>
            <ShareButton />
          </Container>
        </ButtonsContainer>
        <Typography variant="h1" component="h1">
          {data.modelName}
        </Typography>
        <DescrBox>
          <Typography variant="body2">{carUmbraco?.topInfoTitle}</Typography>
        </DescrBox>
        <ContentContainer>
          {data.carZone.name.carDetails && (
            <DescriptionContainer>
              <Typography variant="body2">
                {data.carZone.name.carDetails}
              </Typography>
            </DescriptionContainer>
          )}
          <CardsGrid
            as="ul"
            role="list"
            itemCount={items.length}
            aria-label="מפרט טכני"
          >
            {items.map((el, index) => (
              <Card as="li" key={index}>
                <IconbBox>
                  <ImageContainer>
                    <Image
                      src={getUmbracoMediaUrl(el.icon)}
                      alt=""
                      aria-hidden="true"
                      fill
                    />
                  </ImageContainer>
                </IconbBox>
                <CardTextBox>
                  <Typography variant="body3">{el.title}</Typography>
                  <Desktop centerHorizontally>
                    <Typography variant="body1" fontWeight={600}>
                      {el.value}
                    </Typography>
                  </Desktop>
                  <Mobile centerHorizontally>
                    <Typography variant="body2" fontWeight={600}>
                      {el.value}
                    </Typography>
                  </Mobile>
                </CardTextBox>
              </Card>
            ))}
          </CardsGrid>
          <StyledButton
            variant="secondary"
            size="large"
            href={`/search?dealType=${dealType}&carType=${carType}&manufacturer=${data.masterManufacturerCode || data.manufacturerCode}`}
            onClick={() =>
              ctaVehicleClicked(`sale-${data.vehicleNumber}`, "sale_car_page")
            }
          >
            לכל דגמי {data.manufacturerName}
          </StyledButton>
        </ContentContainer>
      </StyledContainer>
    </Container>
  )
}
